import { useState } from "react"

import { parseChartSpec, type CovalChartSpecV1 } from "@/app-covi"
import { Checkbox, FormField, Input } from "@coval/ui"

import type { Dataset, NotebookSection } from "../../utils/reportTypes"
import { Choice } from "./ReportControls"
import { reportClass } from "./ReportStyles"

const CHART_TYPES = ["line", "bar", "area", "scatter", "pie", "stat"] as const
const CHART_COLORS = [
  "primary",
  "blue",
  "green",
  "red",
  "pink",
  "amber",
  "muted",
] as const satisfies readonly NonNullable<
  CovalChartSpecV1["series"][number]["color"]
>[]

export function ReportChartEditor({
  section,
  datasets,
  onChange,
  disabled,
}: {
  section: NotebookSection
  datasets: Dataset[]
  onChange: (section: NotebookSection) => void
  disabled: boolean
}) {
  const [error, setError] = useState("")
  const chart = section.chart
  if (!chart) {
    return null
  }
  const update = (patch: Partial<CovalChartSpecV1>) => {
    const candidate = { ...chart, ...patch }
    if (!parseChartSpec(candidate, { allowReportColors: true })) {
      setError(
        "This combination cannot be rendered. Choose an axis and at least one numeric series.",
      )
      return
    }
    setError("")
    onChange({ ...section, chart: candidate })
  }
  const originalSeries =
    datasets.find((item) => item.id === section.chart_dataset_id)?.series ??
    chart.series
  return (
    <div className={reportClass("chart-editor")}>
      {datasets.length > 0 && (
        <Choice
          label="Verified dataset"
          disabled={disabled}
          value={section.chart_dataset_id ?? datasets[0].id}
          options={datasets.map((data) => ({
            value: data.id,
            label: data.title,
          }))}
          onChange={(id) => {
            const dataset = datasets.find((item) => item.id === id)
            if (!dataset) {
              return
            }
            const candidate = {
              ...chart,
              data: dataset.data,
              series: dataset.series,
              x: dataset.x,
              y: dataset.y,
              title: dataset.title,
            }
            if (!parseChartSpec(candidate, { allowReportColors: true })) {
              setError(
                "Choose a compatible chart type before switching datasets.",
              )
              return
            }
            setError("")
            onChange({
              ...section,
              chart_dataset_id: id,
              chart: candidate,
              evidence_ids: dataset.evidence_ids,
            })
          }}
        />
      )}
      <div className={reportClass("chart-controls")}>
        <Choice
          label="Chart type"
          disabled={disabled}
          value={chart.chart}
          options={CHART_TYPES.map((value) => ({
            value,
            label: value[0].toUpperCase() + value.slice(1),
          }))}
          onChange={(type) =>
            update({ chart: type as CovalChartSpecV1["chart"] })
          }
        />
        {chart.chart !== "stat" && (
          <FormField label="X axis">
            {(ids) => (
              <Input
                {...ids}
                aria-label="Chart X axis"
                value={chart.x?.label ?? chart.x?.field ?? ""}
                readOnly
              />
            )}
          </FormField>
        )}
      </div>
      <FormField label="Chart title">
        {(ids) => (
          <Input
            {...ids}
            aria-label="Chart title"
            disabled={disabled}
            value={chart.title}
            onChange={(event) => update({ title: event.target.value })}
          />
        )}
      </FormField>
      <div className={reportClass("series-fields")}>
        <span className={reportClass("field-label")}>Series</span>
        {originalSeries.map((original) => (
          <label className={reportClass("check-row")} key={original.field}>
            <Checkbox
              aria-label={`Show ${original.name ?? original.field}`}
              disabled={disabled}
              checked={chart.series.some(
                (series) => series.field === original.field,
              )}
              onCheckedChange={() => {
                const existing = chart.series.find(
                  (series) => series.field === original.field,
                )
                update({
                  series: existing
                    ? chart.series.filter(
                        (series) => series.field !== original.field,
                      )
                    : [...chart.series, original],
                })
              }}
            />
            <span>{original.name ?? original.field.replaceAll("_", " ")}</span>
          </label>
        ))}
      </div>
      {chart.series.map((series) => (
        <Choice
          key={series.field}
          label={`${series.name ?? series.field} color`}
          disabled={disabled}
          value={series.color ?? "automatic"}
          options={[
            { value: "automatic", label: "Automatic" },
            ...CHART_COLORS.map((value) => ({
              value,
              label: value[0].toUpperCase() + value.slice(1),
            })),
          ]}
          onChange={(color) =>
            update({
              series: chart.series.map((entry) =>
                entry.field === series.field
                  ? {
                      ...entry,
                      color:
                        color === "automatic"
                          ? undefined
                          : (color as (typeof CHART_COLORS)[number]),
                    }
                  : entry,
              ),
            })
          }
        />
      ))}
      {chart.chart === "bar" && (
        <label className={reportClass("check-row")}>
          <Checkbox
            aria-label="Horizontal bars"
            disabled={disabled}
            checked={chart.opts?.horizontal === true}
            onCheckedChange={(checked) =>
              update({ opts: { ...chart.opts, horizontal: Boolean(checked) } })
            }
          />
          <span>Horizontal bars</span>
        </label>
      )}
      {error && (
        <p className={reportClass("inline-error")} role="alert">
          {error}
        </p>
      )}
      <p className={reportClass("field-hint")}>
        Values remain tied to the verified dataset. Ask Sofia to change the
        analysis or choose another dataset.
      </p>
    </div>
  )
}
